import React, { useState, useRef } from "react";
import EmojiPicker, { Emoji } from "emoji-picker-react";
import KoDP from "packages/dp";
import { KoText, KoTitle } from "packages/typography";
import {
  KoDateFormatter,
  KoPopOver,
  KoButton,
  KoLoader,
  KoTag,
  Upload,
  KoToast
} from "packages";
import KoIcon from "@kwapio/icons";
import styles from "./comments.module.scss";
import DocumentCreate from "pages/drive/documentcreate";
import KoRichTextEditorReadOnly from "packages/richText/richTextView";
import { getCookie } from "shared/utils/cookies";

const KoComments = ({
  source,
  className,
  workspaceId,
  ticketId,
  data,
  setComment,
  onReaction,
  onReply,
  onDelete,
  onUploadFiles,
  onEdit,
  lookupId
}) => {
  const [currentComment, setCurrentComment] = useState(null);
  const [openFileCreate, setOpenFileCreate] = useState(false);
  const [currentRecord, setCurrentRecord] = useState();
  const [loader, setLoader] = useState(false);

  const commentRefs = useRef({});

  const onEmojiClick = (emojiObject) => {
    onReaction(emojiObject?.unified, currentComment);
  };

  const scrollToComment = (commentId) => {
    const targetRef = commentRefs.current[commentId];
    if (targetRef) {
      targetRef.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  let dataObject = {
    module: source ? source : "comments",
    folderId: ticketId,
    lookupId: currentComment?.commentId
  };

  if (workspaceId) {
    dataObject.service = workspaceId;
  }

  const props = {
    name: "files",
    multiple: true,
    action: `${process.env.REACT_APP_PROXY_HOST}/api/v1/org/upload`,
    data: dataObject,
    headers: {
      Authorization: `Bearer ${getCookie("accessToken")}`
    },
    onChange(info) {
      setLoader(true);
      if (info.file.status === "done") {
        setLoader(false);
        onUploadFiles(currentComment, info.fileList);
        KoToast.success(`${info.file.name} file uploaded successfully`);
      } else if (info.file.status === "error") {
        setLoader(false);
        KoToast.error(`${info.file.name} file upload failed.`);
      }
    }
  };

  const emojiContent = (
    <div className={styles.popContent}>
      <EmojiPicker
        Theme="auto"
        height={450}
        width={350}
        emojiStyle="native"
        onEmojiClick={onEmojiClick}
        previewConfig={{ showPreview: false }}
        suggestedEmojisMode="recent"
        skinTonesDisabled={true}
        reactionsDefaultOpen={true}
      />
    </div>
  );

  const getParentComment = (comment) => {
    if (comment?.parentCommentId) {
      return data?.find(
        (item) => item?.commentId === comment?.parentCommentId
      );
    }
  };

  const onRemoveFile = () => {};

  return (
    <div className={`${styles.wrapper} ${className ? className : ""}`}>
      {loader && <KoLoader />}

      {data.map((comment, index) => {
        return (
          <div
            key={index}
            onClick={() => setComment(comment)}
            className={styles.commentContainer}
            ref={(el) =>
              (commentRefs.current[comment.commentId] = el)
            }
          >
            <div className={styles.commentPic}>
              <KoDP
                width={36}
                height={36}
                logo={comment?.profile?.logo}
              />
            </div>

            <div className={styles.commentWrapper}>
              <div className={styles.commentHeader}>
                <div className={styles.commentName}>
                  <KoTitle
                    level={5}
                    className={styles.name}
                    text={comment?.profile?.firstName}
                  />
                  <KoTitle
                    level={5}
                    className={styles.name}
                    text={comment?.profile?.lastName}
                  />
                </div>
                <div className={styles.dot} />
                <KoDateFormatter
                  data={comment?.createdAt}
                  format={"hh:mm A"}
                />
              </div>

              <div className={styles.comment}>
                {comment?.parentCommentId && (
                  <div
                    className={styles.parentCommentBody}
                    onClick={() =>
                      scrollToComment(comment?.parentCommentId)
                    }
                  >
                    <div className={styles.commentHeader}>
                      <div className={styles.commentPic}>
                        <KoDP
                          width={25}
                          height={25}
                          logo={
                            getParentComment(comment)?.profile
                              ?.logo
                          }
                        />
                      </div>
                      <div className={styles.commentName}>
                        <KoTitle
                          level={5}
                          className={styles.name}
                          text={
                            getParentComment(comment)?.profile
                              ?.firstName
                          }
                        />
                        <KoTitle
                          level={5}
                          className={styles.name}
                          text={
                            getParentComment(comment)?.profile
                              ?.lastName
                          }
                        />
                      </div>
                    </div>

                    <div className={styles.commentBody}>
                      <KoRichTextEditorReadOnly
                        initialValues={
                          getParentComment(comment)?.content
                        }
                      />
                    </div>
                  </div>
                )}

                <div className={styles.element}>
                  <KoRichTextEditorReadOnly
                    initialValues={comment?.content}
                    attachments={comment?.attachments}
                  />
                </div>

                <div className={styles.fileContainer}>
                  {comment?.attachments?.map((file, index) => (
                    <KoTag
                      key={index}
                      value={file?.name}
                      color="blue"
                      onClick={() => {
                        setCurrentRecord(file);
                        setOpenFileCreate(true);
                      }}
                    />
                  ))}
                </div>

                {openFileCreate && (
                  <DocumentCreate
                    openFileCreate={openFileCreate}
                    source={"attachments"}
                    fileObject={currentRecord}
                    showComments={false}
                    setOpenFileCreate={setOpenFileCreate}
                    documentId={currentRecord?.documentId}
                  />
                )}

                <div className={styles.commentFooter}>
                  <div className={styles.replyContainer}>
                    <KoButton
                      onClick={() => onReply(comment)}
                      type="text"
                      iconBtn
                      shape="circle"
                      icon={
                        <KoIcon
                          width={18}
                          height={18}
                          name="ReplyOutlined"
                        />
                      }
                    />
                    <KoPopOver
                      placement="bottomLeft"
                      arrow={false}
                      content={emojiContent}
                      trigger="click"
                    >
                      <KoButton
                        onClick={() =>
                          setCurrentComment(comment)
                        }
                        type="text"
                        iconBtn
                        shape="circle"
                        icon={
                          <KoIcon
                            width={18}
                            height={18}
                            name="SmileyOutlined"
                          />
                        }
                      />
                    </KoPopOver>
                    <KoButton
                      onClick={() => onEdit(comment)}
                      type="text"
                      iconBtn
                      shape="circle"
                      icon={
                        <KoIcon
                          width={18}
                          height={18}
                          name="EditOutlined"
                        />
                      }
                    />
                    <Upload
                      {...props}
                      onRemove={onRemoveFile}
                      showUploadList={false}
                    >
                      <KoButton
                        onClick={() =>
                          setCurrentComment(comment)
                        }
                        type="text"
                        iconBtn
                        shape="circle"
                        icon={
                          <KoIcon
                            width={18}
                            height={18}
                            name="AttachmentOutlined"
                          />
                        }
                      />
                    </Upload>
                    <KoButton
                      onClick={() => onDelete(comment)}
                      type="text"
                      iconBtn
                      shape="circle"
                      icon={
                        <KoIcon
                          width={16}
                          height={16}
                          name="DeleteOutlined"
                        />
                      }
                    />
                  </div>

                  {comment?.reactions?.length > 0 && <div className={styles.reactionsContainer}>
                    <div className={styles.reactions}>
                      {comment?.reactions?.map(
                        (reactObj, index) => (
                          <div
                            className={styles.reaction}
                            key={index}
                          >
                            <Emoji
                              size={17}
                              unified={reactObj?.reaction}
                            />
                            <KoText
                              text={reactObj?.count}
                              className={
                                styles.reactionCount
                              }
                            />
                          </div>
                        )
                      )}
                    </div>
                  </div>}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default KoComments;
